import React, { useState, useEffect } from 'react';
import { showSuccess, showError } from '../utils/toast';
import LoadingSpinner from './LoadingSpinner';
import Button from './ui/Button';
import API_URL from '../config';
import { formatDateTime } from '../utils/formatDateTime';
import './AvaliacaoEficacia.css';

// Helper: fetch com token JWT automático
function authFetch(url, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

function AvaliacaoEficacia({ ncId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    eficacia_avaliada: 'pendente',
    responsavel_avaliacao: '',
    justificativa_eficacia: '',
    data_avaliacao_eficacia: null,
    data_prevista_avaliacao: '',
    requer_alteracao_sgq: 'nao',
    documentos_sgq_alterados: '',
    requer_reavaliacao_riscos: 'nao',
    reavaliacao_riscos_oportunidades: ''
  });
  // Sessão 41 — critérios de eficácia sugeridos pela IA (verificacao_eficacia),
  // editáveis na própria avaliação. null = sem critérios (ex.: modo Registro).
  const [criterios, setCriterios] = useState(null);
  // #95-A — reabrir ciclo quando a ação não resolveu (ineficaz/parcial).
  const [reabrirData, setReabrirData] = useState('');
  const [reabrindo, setReabrindo] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ncId]);

  const fetchData = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${ncId}/eficacia`);
      if (response.ok) {
        const result = await response.json();
        setData({
          eficacia_avaliada: result.eficacia_avaliada || 'pendente',
          responsavel_avaliacao: result.responsavel_avaliacao || '',
          justificativa_eficacia: result.justificativa_eficacia || '',
          data_avaliacao_eficacia: result.data_avaliacao_eficacia,
          data_prevista_avaliacao: result.data_prevista_avaliacao ? result.data_prevista_avaliacao.split('T')[0] : '',
          requer_alteracao_sgq: result.requer_alteracao_sgq || 'nao',
          documentos_sgq_alterados: result.documentos_sgq_alterados || '',
          requer_reavaliacao_riscos: result.requer_reavaliacao_riscos || 'nao',
          reavaliacao_riscos_oportunidades: result.reavaliacao_riscos_oportunidades || ''
        });
        setCriterios(result.verificacao_eficacia || null);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEficacia = async () => {
    if (!data.justificativa_eficacia.trim()) {
      showError('Justificativa é obrigatória');
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${ncId}/avaliar-eficacia`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eficacia_avaliada: data.eficacia_avaliada,
          justificativa_eficacia: data.justificativa_eficacia,
          data_prevista_avaliacao: data.data_prevista_avaliacao || null
        })
      });

      if (response.ok) {
        showSuccess('Avaliação de eficácia salva com sucesso!');
        fetchData();
      } else {
        const error = await response.json();
        showError(error.detail || 'Erro ao salvar avaliação');
      }
    } catch (error) {
      console.error('Erro:', error);
      showError('Erro ao salvar avaliação');
    } finally {
      setSaving(false);
    }
  };

  // #95-A — reabre o ciclo quando a ação não resolveu (loop ISO 10.2 / D6→D5).
  // Volta a 'pendente' com nova data prevista (re-arma o alerta D-3); a tentativa
  // anterior fica na trilha. A nova ação corretiva é registrada na análise da NC.
  const handleReabrirCiclo = async () => {
    if (!reabrirData) {
      showError('Informe a nova data prevista para a reavaliação');
      return;
    }
    setReabrindo(true);
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${ncId}/reabrir-eficacia`, {
        method: 'POST',
        body: JSON.stringify({ nova_data_prevista_avaliacao: reabrirData }),
      });
      if (response.ok) {
        showSuccess('Ciclo reaberto. Registre a nova ação corretiva na análise da NC.');
        setReabrirData('');
        fetchData();
      } else {
        const error = await response.json();
        showError(error.detail || 'Erro ao reabrir o ciclo');
      }
    } catch (error) {
      console.error('Erro:', error);
      showError('Erro ao reabrir o ciclo de eficácia');
    } finally {
      setReabrindo(false);
    }
  };

  const handleSaveSGQ = async () => {
    if (data.requer_alteracao_sgq === 'sim' && !data.documentos_sgq_alterados.trim()) {
      showError('Descreva os documentos alterados');
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${ncId}/sgq`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requer_alteracao_sgq: data.requer_alteracao_sgq,
          documentos_sgq_alterados: data.documentos_sgq_alterados
        })
      });

      if (response.ok) {
        showSuccess('Informações do SGQ salvas com sucesso!');
        fetchData();
      } else {
        const error = await response.json();
        showError(error.detail || 'Erro ao salvar SGQ');
      }
    } catch (error) {
      console.error('Erro:', error);
      showError('Erro ao salvar SGQ');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRiscos = async () => {
    if (data.requer_reavaliacao_riscos === 'sim' && !data.reavaliacao_riscos_oportunidades.trim()) {
      showError('Descreva a reavaliação de riscos/oportunidades');
      return;
    }

    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${ncId}/riscos`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requer_reavaliacao_riscos: data.requer_reavaliacao_riscos,
          reavaliacao_riscos_oportunidades: data.reavaliacao_riscos_oportunidades
        })
      });

      if (response.ok) {
        showSuccess('Reavaliação de riscos e oportunidades salva com sucesso!');
        fetchData();
      } else {
        const error = await response.json();
        showError(error.detail || 'Erro ao salvar reavaliação de riscos');
      }
    } catch (error) {
      console.error('Erro:', error);
      showError('Erro ao salvar reavaliação de riscos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Critérios da IA preenchidos — read-only AQUI (a edição é no modo edição da
  // NC, como os demais campos da análise). Mostra só os que têm valor.
  const CAMPOS_CRITERIO = [
    { k: 'criterio', label: 'Critério' },
    { k: 'meta', label: 'Meta' },
    { k: 'metodo', label: 'Método' },
    { k: 'prazo_verificacao', label: 'Prazo de verificação' },
  ];
  const criteriosPreenchidos = criterios
    ? CAMPOS_CRITERIO.filter(({ k }) => (criterios[k] || '').toString().trim())
    : [];

  return (
    <div className="avaliacao-eficacia-container">
      <h3>
        <span className="material-symbols-outlined ae-heading-icon">analytics</span>
        Avaliação de Eficácia das Ações
      </h3>

      {/* Seção de Avaliação de Eficácia */}
      <div className="eficacia-section">
        {/* Sessão 41 — critérios definidos pela IA, exibidos (read-only) pra o
            gestor avaliar CONTRA eles (fecha o ciclo ISO 10.2). A edição é no
            modo edição da NC, como os demais campos. Só Investigação tem. */}
        {criteriosPreenchidos.length > 0 && (
          <div className="eficacia-criterios" style={{ marginBottom: '20px', padding: '14px 16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
            <div style={{ fontWeight: 700, fontSize: '13px', color: '#0d7c3f', marginBottom: '10px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'text-bottom', marginRight: '4px' }}>checklist</span>
              Critérios de eficácia definidos
              {/* #202: link direto pra seção de eficácia do editor (em vez de só
                  dizer "use a edição da NC" e deixar o usuário procurar). O editor
                  só existe quando a NC está em rascunho — fallback explica isso. */}
              <button
                type="button"
                onClick={() => {
                  const alvo = document.getElementById('editor-secao-eficacia');
                  if (alvo) {
                    alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  } else {
                    window.alert('A edição dos critérios fica na seção "Verificação de Eficácia" do editor da análise — disponível enquanto a NC está em rascunho.');
                  }
                }}
                style={{ background: 'none', border: 'none', padding: 0, marginLeft: '6px', fontWeight: 400, fontSize: '12px', color: '#0d7c3f', textDecoration: 'underline', cursor: 'pointer' }}
              >
                editar critérios
              </button>
            </div>
            {criteriosPreenchidos.map(({ k, label }) => (
              <div key={k} style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#0d7c3f', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
                <div style={{ fontSize: '13px', color: '#1f2937', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{criterios[k]}</div>
              </div>
            ))}
          </div>
        )}

        <div className="form-group">
          <label>{criteriosPreenchidos.length > 0 ? 'As ações atingiram os critérios acima? *' : 'Status da Eficácia *'}</label>
          <select
            value={data.eficacia_avaliada}
            onChange={(e) => setData({ ...data, eficacia_avaliada: e.target.value })}
            className="form-select"
          >
            <option value="pendente">⏳ Pendente</option>
            <option value="eficaz">✅ Eficaz</option>
            <option value="parcialmente_eficaz">⚠️ Parcialmente Eficaz</option>
            <option value="ineficaz">❌ Ineficaz</option>
          </select>
          <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
            A ação resolveu? Eficaz = resolveu de vez; Parcialmente = melhorou mas não eliminou; Ineficaz = não resolveu (reabre o ciclo).
          </small>
        </div>

        <div className="form-group">
          <label>Data Prevista para Avaliação</label>
          <input
            type="date"
            value={data.data_prevista_avaliacao}
            onChange={(e) => setData({ ...data, data_prevista_avaliacao: e.target.value })}
            className="form-input"
          />
          <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>lightbulb</span>
            Você receberá notificação 3 dias antes desta data
          </small>
        </div>

        <div className="form-group">
          <label>Justificativa, Explicações e Evidências *</label>
          <textarea
            value={data.justificativa_eficacia}
            onChange={(e) => setData({ ...data, justificativa_eficacia: e.target.value })}
            placeholder="Ex: 90 dias após a troca, zero reclamações; antes eram 3/mês."
            className="form-textarea"
            rows="6"
          />
          <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
            Explique <strong>como</strong> concluiu, com números e fatos. É o que o auditor quer ver.
          </small>
        </div>

        {data.data_avaliacao_eficacia && (
          <div className="info-box">
            <small>
              {/* Decisão 2 Sessão 22: timezone do tenant via formatDateTime
                  (lê localStorage.user.client_timezone, default BRT). */}
              <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom', marginRight: '4px' }}>event</span>
              Última avaliação: {formatDateTime(data.data_avaliacao_eficacia)}
            </small>
          </div>
        )}

        <Button
          variant="primary"
          onClick={handleSaveEficacia}
          disabled={saving}
          icon={saving ? undefined : 'save'}
        >
          {saving ? <LoadingSpinner size="small" /> : 'Salvar Avaliação'}
        </Button>

        {/* #95-A — Reabrir ciclo: aparece quando a avaliação SALVA foi ineficaz
            ou parcialmente eficaz. Fecha o loop ISO 10.2 (a ação não resolveu →
            nova ação + nova reavaliação). */}
        {data.data_avaliacao_eficacia &&
          (data.eficacia_avaliada === 'ineficaz' || data.eficacia_avaliada === 'parcialmente_eficaz') && (
          <div className="eficacia-reabrir" style={{ marginTop: '24px', padding: '16px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px' }}>
            <div style={{ fontWeight: 700, fontSize: '14px', color: '#9a3412', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>replay</span>
              A ação não resolveu — reabrir o ciclo
            </div>
            <p style={{ fontSize: '13px', color: '#7c2d12', margin: '0 0 14px', lineHeight: 1.5 }}>
              Como a ação foi <strong>{data.eficacia_avaliada === 'ineficaz' ? 'ineficaz' : 'parcialmente eficaz'}</strong>,
              registre a <strong>nova ação corretiva</strong> na análise da NC e defina uma nova data de reavaliação.
              Esta avaliação fica registrada na trilha e você volta a ser lembrado 3 dias antes do novo prazo.
            </p>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label>Nova data prevista para reavaliação *</label>
              <input
                type="date"
                value={reabrirData}
                onChange={(e) => setReabrirData(e.target.value)}
                className="form-input"
              />
            </div>
            <Button
              variant="secondary"
              onClick={handleReabrirCiclo}
              disabled={reabrindo || !reabrirData}
              icon={reabrindo ? undefined : 'replay'}
            >
              {reabrindo ? <LoadingSpinner size="small" /> : 'Reabrir ciclo de eficácia'}
            </Button>
          </div>
        )}
      </div>

      {/* Seção de SGQ */}
      <div className="sgq-section">
        <h4>
          <span className="material-symbols-outlined ae-heading-icon">description</span>
          Alteração de Documentos do SGQ
        </h4>

        <div className="form-group">
          <label>Há necessidade de alteração de documento do SGQ? *</label>
          <small style={{ fontSize: '11px', color: '#57657a', margin: '0 0 8px', display: 'block' }}>
            SGQ = Sistema de Gestão da Qualidade — os procedimentos, instruções e formulários da sua empresa. A correção exigiu mudar algum deles?
          </small>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                value="sim"
                checked={data.requer_alteracao_sgq === 'sim'}
                onChange={(e) => setData({ ...data, requer_alteracao_sgq: e.target.value })}
              />
              <span>Sim</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                value="nao"
                checked={data.requer_alteracao_sgq === 'nao'}
                onChange={(e) => setData({ ...data, requer_alteracao_sgq: e.target.value, documentos_sgq_alterados: '' })}
              />
              <span>Não</span>
            </label>
          </div>
        </div>

        {data.requer_alteracao_sgq === 'sim' && (
          <div className="form-group">
            <label>Documentos Alterados *</label>
            <textarea
              value={data.documentos_sgq_alterados}
              onChange={(e) => setData({ ...data, documentos_sgq_alterados: e.target.value })}
              placeholder="Descreva os documentos do SGQ que foram alterados..."
              className="form-textarea"
              rows="4"
            />
          </div>
        )}

        <Button
          variant="primary"
          onClick={handleSaveSGQ}
          disabled={saving}
          icon={saving ? undefined : 'save'}
        >
          {saving ? <LoadingSpinner size="small" /> : 'Salvar SGQ'}
        </Button>
      </div>

      {/* Seção de Reavaliação de Riscos e Oportunidades (ISO 10.2.1 e2) */}
      <div className="sgq-section">
        <h4>
          <span className="material-symbols-outlined ae-heading-icon">policy</span>
          Reavaliação de Riscos e Oportunidades
        </h4>

        <div className="form-group">
          <label>A NC gerou reavaliação de riscos/oportunidades (novo risco, mudança ou oportunidade)? *</label>
          <small style={{ fontSize: '11px', color: '#57657a', margin: '0 0 8px', display: 'block' }}>
            Esse problema revelou um risco novo (ou uma oportunidade) que a empresa ainda não tinha mapeado? Se sim, vale atualizar o mapeamento de riscos da qualidade (o "item 6.1" da ISO 9001).
          </small>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                value="sim"
                checked={data.requer_reavaliacao_riscos === 'sim'}
                onChange={(e) => setData({ ...data, requer_reavaliacao_riscos: e.target.value })}
              />
              <span>Sim</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                value="nao"
                checked={data.requer_reavaliacao_riscos === 'nao'}
                onChange={(e) => setData({ ...data, requer_reavaliacao_riscos: e.target.value, reavaliacao_riscos_oportunidades: '' })}
              />
              <span>Não</span>
            </label>
          </div>
        </div>

        {data.requer_reavaliacao_riscos === 'sim' && (
          <div className="form-group">
            <label>Descrição da reavaliação *</label>
            <textarea
              value={data.reavaliacao_riscos_oportunidades}
              onChange={(e) => setData({ ...data, reavaliacao_riscos_oportunidades: e.target.value })}
              placeholder="Novos riscos identificados, mudanças em riscos existentes ou oportunidades de melhoria (atualização do mapeamento de riscos — item 6.1)..."
              className="form-textarea"
              rows="4"
            />
          </div>
        )}

        <Button
          variant="primary"
          onClick={handleSaveRiscos}
          disabled={saving}
          icon={saving ? undefined : 'save'}
        >
          {saving ? <LoadingSpinner size="small" /> : 'Salvar Reavaliação'}
        </Button>
      </div>
    </div>
  );
}

export default AvaliacaoEficacia;
